import { LayoutInfo, Rect, Size } from 'react-stately/useVirtualizerState';
import {
  LayoutNode,
  ListLayoutOptions,
} from 'react-stately/useVirtualizerState';
import { Node } from '@react-types/shared';
import { Key } from 'react';
import { VariableWidthListLayout } from './VariableWidthListLayout';

interface ListWithLoadingStateLayoutProps {
  loadingState?: 'loading' | 'loadingMore';
  loadingRowsCount: number;
  sectionDividerHeight: number;
  headingHeight: number;
}

export class ListWithLoadingStateLayout<T> extends VariableWidthListLayout<T> {
  readonly options;

  constructor(
    options: Pick<ListLayoutOptions, 'rowHeight' | 'estimatedRowHeight'> &
      ListWithLoadingStateLayoutProps
  ) {
    super(options);
    this.options = options;

    this.loaderSize =
      options.loadingRowsCount *
      (options.rowHeight ?? options.estimatedRowHeight ?? 50);
  }

  override doBuildCollection(): LayoutNode[] {
    let nodes: LayoutNode[] = [];
    if (this.options.loadingState === 'loading' || this.collection.size === 0) {
      this.contentSize.height = 0;
    } else {
      nodes = super.doBuildCollection();
    }
    if (this.options.loadingState) {
      const loaderNode = this.buildLoader();
      nodes.push(loaderNode);
      this.contentSize.height =
        this.contentSize.height + loaderNode.layoutInfo.rect.height;
    }

    if (!this.options.loadingState && this.collection.size === 0) {
      const emptyStateNode = this.buildEmptyState();
      nodes.push(emptyStateNode);
      this.contentSize.height = emptyStateNode.layoutInfo.rect.height;
    }
    return nodes;
  }

  /**
   * Adds the section header node (which is rendered as a divider and
   * optional heading text)
   */
  override buildSection(node: Node<T>, x: number, y: number): LayoutNode {
    // Synthesize a collection node for the header.
    const headerNode = {
      type: 'header',
      key: node.key + ':header',
      parentKey: node.key,
      value: null,
      level: node.level,
      index: node.index,
      hasChildNodes: false,
      childNodes: [],
      rendered: node.rendered,
      textValue: node.textValue,
    };

    // `node.rendered` is undefined if Section doesn't have a title.
    // `this.headingSize` is used in `this.buildSectionHeader`
    this.headingSize = node.rendered ? this.options.headingHeight : 0;

    // Build layout node for it and adjust y offset of section children.
    const header = this.buildSectionHeader(headerNode, x, y);
    header.node = headerNode;
    header.layoutInfo.parentKey = node.key;
    this.layoutNodes.set(headerNode.key, header);
    header.layoutInfo.rect.height +=
      // if first item is the section, we don't show the divider
      node.prevKey ? this.options.sectionDividerHeight : 0;

    y += header.layoutInfo.rect.height;

    const section = super.buildSection(node, x, y);
    section.children!.unshift(header);

    // Add a separator after the section if it is not followed by a section;
    if (node.nextKey && node.index !== undefined) {
      const nextNode = this.nextNodeAtLevel(node.level, node.index);
      if (nextNode && nextNode.type !== 'section') {
        const dividerRect = new Rect(
          0,
          section.layoutInfo.rect.maxY,
          this.virtualizer?.visibleRect.width,
          this.options.sectionDividerHeight
        );

        const layoutInfo = new LayoutInfo(
          'divider',
          `${node.key}:divider`,
          dividerRect
        );
        layoutInfo.parentKey = node.key;

        const layoutNode = {
          layoutInfo: layoutInfo,
          validRect: layoutInfo.rect.intersection(this.validRect),
        };

        this.layoutNodes.set(layoutInfo.key, layoutNode);
        section.children?.push(layoutNode);

        section.layoutInfo.rect.height += dividerRect.height;
        y += dividerRect.height;
      }
    }

    return section;
  }

  nextNodeAtLevel(level: number, startAt: number): Node<T> | undefined {
    for (const item of this.collection) {
      if (item.level === level && item.index && item.index > startAt) {
        return item;
      }
    }
  }

  /**
   * Builds loader node (containing all loading rows at the end of the view)
   */
  buildLoader(): LayoutNode {
    const rect = new Rect(
      0,
      this.contentSize.height,
      this.virtualizer?.visibleRect.width,
      this.loaderSize ?? 0
    );
    const previousLayoutNode = this.layoutNodes.get('loader');
    if (previousLayoutNode?.layoutInfo.rect.y === rect.y) {
      return previousLayoutNode;
    }
    const layoutNode = {
      layoutInfo: new LayoutInfo('loader', 'loader', rect),
      validRect: rect,
    };
    layoutNode.layoutInfo.estimatedSize = true;
    this.layoutNodes.set('loader', layoutNode);
    return layoutNode;
  }

  /**
   * Builds empty state node
   */
  buildEmptyState(): LayoutNode {
    const rect = new Rect(
      0,
      this.contentSize.height,
      this.virtualizer?.visibleRect.width,
      this.options.rowHeight ?? this.options.estimatedRowHeight ?? 50
    );
    const previousLayoutNode = this.layoutNodes.get('empty-state');
    if (previousLayoutNode?.layoutInfo.rect.y === rect.y) {
      return previousLayoutNode;
    }
    const layoutNode = {
      layoutInfo: new LayoutInfo('empty-state', 'empty-state', rect),
      validRect: rect,
    };
    layoutNode.layoutInfo.estimatedSize = true;
    this.layoutNodes.set('empty-state', layoutNode);
    return layoutNode;
  }

  /**
   * estimatedSize is set to false by default after size is updated, assuming item
   * height is not dynamic. In some cases (including loading skeleton rows) the height can
   * change as custom loading skeleton render can be passed as a prop. Setting estimatedSize to
   * true makes sure size is calculated and updated if needed after each render.
   * See useVirtualizerItem code for more details.
   */
  override updateItemSize(key: Key, size: Size): boolean {
    const result = super.updateItemSize(key, size);
    if (this.canNodeHaveVariableHeight(key)) {
      const loaderLayoutInfo = this.virtualizer
        ?.getVisibleLayoutInfos()
        .get(key);
      if (loaderLayoutInfo) {
        loaderLayoutInfo.estimatedSize = true;
      }
    }
    return result;
  }

  private canNodeHaveVariableHeight(key: Key) {
    return key === 'loader';
  }
}
